import { ThrowLiteError } from '../lite-error.js';
import { acquireGPUTexture, releaseGPUTexture } from '../resource/gpu-pool.js';
import { b as assembleEnvironmentTextures, a as loadBrdfImage } from '../_chunks/env-helpers-CRI1i-A3.js';
import { parseEnvFile } from './env-parse.js';

async function rebuildSceneEnvironment(engine, scene) {
  const current = scene._envTextures;
  if (!current) {
    return;
  }
  const source = scene._envRecoverySource;
  if (!source) {
    ThrowLiteError(114);
  }
  const sphericalHarmonics = current.sphericalHarmonics;
  scene._envTextures = void 0;
  let replacement;
  if (source.kind === "env") {
    replacement = await reloadEnvironmentTextures(engine, source, sphericalHarmonics);
  } else {
    const parser = await import('../loader-hdr/hdr-parser.js');
    const pipeline = await import('../loader-hdr/hdr-ibl-pipeline.js');
    const buffer = await fetchRecoverySource(source.url);
    const hdr = parser.parseRGBE(buffer);
    const irradianceSH = parser.computeSHFromEquirect(hdr.data, hdr.width, hdr.height);
    const sourceCube = pipeline.equirectToCubemapGPU(engine, hdr, source.faceSize);
    const specularCube2 = pipeline.prefilterCubemapGPU(engine, sourceCube, source.faceSize, Math.floor(Math.log2(source.faceSize)) + 1);
    replacement = assembleEnvironmentTextures(specularCube2, pipeline.generateBrdfLut(engine), irradianceSH, pipeline.HDR_LOD_GENERATION_SCALE, engine, sphericalHarmonics);
  }
  Object.assign(current, replacement);
  const { specularCube, brdfLut } = current;
  acquireGPUTexture(specularCube);
  acquireGPUTexture(brdfLut);
  scene._disposables.push(() => {
    releaseGPUTexture(specularCube);
    releaseGPUTexture(brdfLut);
  });
  scene._envTextures = current;
  scene._envRecoverySource = source;
}
async function reloadEnvironmentTextures(engine, source, sphericalHarmonics) {
  const brdfPromise = loadBrdfImage(source.brdfUrl);
  brdfPromise.catch(() => {
  });
  const buffer = await fetchRecoverySource(source.url);
  const { faceBlobs, irradianceSH, width, mipCount } = parseEnvFile(buffer);
  const faceImages = await Promise.all(faceBlobs.map((blob) => createImageBitmap(blob, { premultiplyAlpha: "none", colorSpaceConversion: "none" })));
  const rgbd = await import('./rgbd-decode.js');
  const specularCube = rgbd.uploadCubemapRGBD(engine, faceImages, width, mipCount);
  for (const image of faceImages) {
    image.close();
  }
  const brdfImage = await brdfPromise;
  const brdfLut = rgbd.decodeBrdfPng(engine, brdfImage);
  brdfImage.close();
  return assembleEnvironmentTextures(specularCube, brdfLut, irradianceSH, 0.8, engine, sphericalHarmonics);
}
async function fetchRecoverySource(url) {
  const response = await fetch(url);
  if (!response.ok) {
    ThrowLiteError(115, url, response.status, response.statusText);
  }
  return response.arrayBuffer();
}

export { rebuildSceneEnvironment };
//# sourceMappingURL=environment-recovery.js.map
